// frontend/src/components/Dashboard/Dashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  CircularProgress,
  Chip, // <<< ADDED Chip IMPORT
  Alert
} from '@mui/material';
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';
import { getUserImages } from '../../api/images'; 
import { getUserById } from '../../api/users';
import ImageIcon from '@mui/icons-material/Image';
import PageviewIcon from '@mui/icons-material/Pageview'; 
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // <<< ADDED CheckCircleOutlineIcon IMPORT
import PersonIcon from '@mui/icons-material/Person';
import ClearIcon from '@mui/icons-material/Clear';
import { useAuth } from '../../contexts/AuthContext';

// Simple Loader component
const Loader = ({ message }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress sx={{ mr: 2 }} />
        <Typography>{message || 'Loading...'}</Typography>
    </Box>
);


const Dashboard = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userIdFilter = searchParams.get('user');
  
  const [loading, setLoading] = useState(true);
  const [filteredUser, setFilteredUser] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    failed_generation: 0, 
    failed_submission: 0, 
    submitted: 0,
    staged: 0, 
    preview: 0, 
    archived: 0, 
    pending: 0, 
  });
  const [recentImages, setRecentImages] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch filtered user info if userIdFilter is present
        if (userIdFilter && user?.is_admin) {
          try {
            const userData = await getUserById(userIdFilter);
            setFilteredUser(userData);
          } catch (error) {
            console.error('Error fetching user data:', error);
            // User not found or no permission
          }
        }
        
        // Fetch images - if userIdFilter is present and user is admin, fetch that user's images
        const params = { limit: 50 }; // Keep reasonable limit to avoid memory issues
        if (userIdFilter && user?.is_admin) {
          params.user_id_filter = userIdFilter;
        }
        
        const images = await getUserImages(params); 

        const total = images.length;
        const success = images.filter(img => img.status === 'success').length;
        const failed_generation = images.filter(img => img.status === 'failed_generation' || img.status === 'failed').length;
        const failed_submission = images.filter(img => img.status === 'failed_submission').length;
        const submitted = images.filter(img => img.is_submitted).length; 
        const staged = images.filter(img => img.is_staged && !img.is_submitted && !img.is_archived).length;
        const preview = images.filter(img => img.status === 'success' && !img.is_staged && !img.is_submitted && !img.is_archived).length;
        const archived = images.filter(img => img.is_archived).length;
        const pending = images.filter(img => ['pending', 'processing', 'pending_async'].includes(img.status)).length;


        setStats({ total, success, failed_generation, failed_submission, submitted, staged, preview, archived, pending });

        const recent = images
            .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
            .slice(0, 5);
        setRecentImages(recent);

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userIdFilter, user]);

  if (loading) {
    return <Loader message="Loading dashboard..." />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Dashboard
        </Typography>
        {filteredUser && (
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={() => navigate('/dashboard')}
            size="small"
          >
            Clear Filter
          </Button>
        )}
      </Box>

      {filteredUser && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          icon={<PersonIcon />}
        >
          Viewing images for user: <strong>{filteredUser.username}</strong> ({filteredUser.email})
        </Alert>
      )}

      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        {filteredUser 
          ? `${filteredUser.username}'s Image Statistics`
          : `Welcome back, ${user?.username || 'User'}!`
        }
      </Typography>

      {/* Quick action buttons */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<ImageIcon />}
            component={RouterLink}
            to="/generate-mrhi" // Updated link
            sx={{ py: 1.5 }}
          >
            Generate New MRHIs
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<PageviewIcon />} // Updated icon
            component={RouterLink}
            to="/manage-images" // Updated link
            sx={{ py: 1.5 }}
          >
            Review & Manage Images
          </Button>
        </Grid>
      </Grid>

      {/* Stats cards */}
      <Typography variant="h6" gutterBottom>
        Your MRHI Statistics
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4} md={2.4}> <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}> <Typography variant="h4">{stats.preview}</Typography> <Typography variant="body2" color="textSecondary">Preview Ready</Typography> </Paper> </Grid>
        <Grid item xs={6} sm={4} md={2.4}> <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.light' }}> <Typography variant="h4" color="white">{stats.staged}</Typography> <Typography variant="body2" color="white">Staged</Typography> </Paper> </Grid>
        <Grid item xs={6} sm={4} md={2.4}> <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.main' }}> <Typography variant="h4" color="white">{stats.submitted}</Typography> <Typography variant="body2" color="white">Submitted</Typography> </Paper> </Grid>
        <Grid item xs={6} sm={4} md={2.4}> <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}> <Typography variant="h4" color="black">{stats.pending}</Typography> <Typography variant="body2" color="black">Processing</Typography> </Paper> </Grid>
        <Grid item xs={6} sm={4} md={2.4}> <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}> <Typography variant="h4" color="white">{stats.failed_generation + stats.failed_submission}</Typography> <Typography variant="body2" color="white">Total Failed</Typography> </Paper> </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        Recent Activity
      </Typography>
      {recentImages.length > 0 ? (
        <Grid container spacing={2}>
          {recentImages.map((image) => (
            <Grid item xs={12} sm={6} md={4} key={image.id}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" noWrap title={image.gtin} gutterBottom>
                    GTIN: {image.gtin}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                      Status:
                    </Typography>
                    {image.is_archived ? <Chip label="Archived" size="small" variant="outlined"/> :
                     image.is_submitted ? <Chip label="Submitted" color="primary" size="small" icon={<CheckCircleIcon />} /> :
                     image.is_staged ? <Chip label="Staged" color="secondary" size="small" /> :
                     image.status === 'success' ? <Chip label="Preview Ready" color="success" size="small" icon={<CheckCircleOutlineIcon />} /> :
                     image.status === 'failed_generation' ? <Chip label="Gen. Failed" color="error" size="small" icon={<ErrorIcon />} /> :
                     image.status === 'failed_submission' ? <Chip label="Sub. Failed" color="error" size="small" icon={<ErrorIcon />} /> :
                     ['pending', 'processing', 'pending_async'].includes(image.status) ? <Chip label="Processing" color="warning" size="small" icon={<HourglassEmptyIcon />} /> :
                     <Chip label={image.status || 'Unknown'} size="small" />
                    }
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Last Updated: {new Date(image.updated_at || image.created_at).toLocaleString()}
                  </Typography>
                </CardContent>
                <Divider />
                <CardActions>
                  <Button size="small" component={RouterLink} to={`/images/${image.id}`}> View Details </Button>
                  <Button size="small" color="secondary" component={RouterLink} to={`/images/${image.id}/edit`}> Edit </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1"> You haven't generated any images yet. </Typography>
          <Button variant="contained" component={RouterLink} to="/generate-mrhi" sx={{ mt: 2 }} >
            Generate Your First MRHI
          </Button>
        </Paper>
      )}

      {stats.total > 0 && (
        <Box textAlign="center" mt={4}>
          <Button
            variant="contained"
            color="secondary"
            component={RouterLink}
            to="/manage-images" 
            size="large"
          >
            Go to Review & Manage All Images
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
